import { Component, computed, input, output, signal } from '@angular/core';

/**
 * The text box and Send. Pure input/output: no service, no HttpClient, no
 * Supabase. `disabled` covers a closed conversation and one this person only
 * watches, and it gates the Send button *and* the Enter key — a keystroke never
 * consults the button's disabled attribute.
 *
 * Every class is prefixed `afs-chat-` and lives in this component's own inline
 * styles, so NobleUI's demo `.chat-*` set cannot reach it.
 */
@Component({
  selector: 'app-chat-composer',
  standalone: true,
  styles: [`
    .afs-chat-composer {
      display: flex; gap: .4rem; padding: .5rem .6rem;
      border-top: 1px solid rgba(0,0,0,.12);
    }
    .afs-chat-composer__input {
      flex: 1; resize: none; border: 1px solid rgba(0,0,0,.2); border-radius: 4px;
      padding: .3rem .45rem; font: inherit; font-size: .83rem;
    }
    /* Touch target: the field team drives this on an Android tablet. */
    @media (pointer: coarse) {
      .afs-chat-composer__input { font-size: 1rem; min-height: 44px; }
      .afs-chat-composer__send { min-height: 44px; }
    }
  `],
  template: `
    <form class="afs-chat-composer" (submit)="$event.preventDefault(); submit()">
      <textarea
        class="afs-chat-composer__input"
        rows="2"
        placeholder="Type a message"
        aria-label="Message"
        [value]="draft()"
        [disabled]="disabled()"
        (input)="onInput($any($event.target).value)"
        (keydown)="onKeydown($event)"
      ></textarea>

      <button
        class="afs-chat-composer__send btn btn-sm btn-primary"
        type="submit"
        [disabled]="!canSend()"
      >Send</button>
    </form>
  `,
})
export class ChatComposerComponent {
  /** The chat is not writable — closed, or one this person only watches. */
  readonly disabled = input<boolean>(false);
  /** A send is already in flight. Set by the panel, cleared when it settles. */
  readonly busy = input<boolean>(false);

  readonly send = output<string>();
  readonly typing = output<void>();

  readonly draft = signal('');

  readonly canSend = computed(
    () => !this.disabled() && !this.busy() && this.draft().trim().length > 0,
  );

  onInput(value: string): void {
    this.draft.set(value);
    // Deleting the last character is not typing at anyone.
    if (value.length > 0) this.typing.emit();
  }

  onKeydown(event: KeyboardEvent): void {
    // Shift+Enter is a new line. A chat box that needs a mouse click to send
    // feels broken to anyone who has used one before.
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    this.submit();
  }

  submit(): void {
    // The whole guard, not just the button's disabled attribute: Enter never
    // consults the button, and a second tap lands before the parent's `busy`
    // has made it back through change detection. Without this a blank line —
    // or the empty string the first send left behind — is posted into the
    // transcript as a message the other person sees.
    if (!this.canSend()) return;
    const body = this.draft().trim();
    this.draft.set('');
    this.send.emit(body);
  }

  /**
   * The box clears optimistically the moment Send is hit, so nothing else can
   * hand the text back. Losing a paragraph typed one-handed on a roof is the
   * failure nobody forgives.
   */
  restore(body: string): void {
    this.draft.set(body);
  }
}
